import PropertyFormGeneric from "../../components/property-form/PropertyFormGeneric";

import {
  useGetDetailPropertyQuery,
  useUpdateRealEstateMutation
} from "@/src-refactor/modules/store/qrtk/services/realestate/realestate.qrtk";
import { UseFormReturn } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Grid, styled } from "@mui/material";
import MxEmptyCard from "@rf-l1/empty-sections/MxEmptyCard";
import MxEmptyIcon from "@rf-l1/empty-sections/MxEmptyIcon";
import MxEmptyBody from "@rf-l1/empty-sections/MxEmptyBody";
import MxLoading from "@rf-l1/loading/MxLoading";
import patchPropertyAdapter from "../../adapters/form/patch-property-adapter";
import UpdatePropertyAdapter from "../../adapters/form/update-property-adapter";
import { C_PATH_ROUTES } from "@/src-refactor/modules/shared/model/const/path.const";
import { useResetAllFormStates } from "../../components/property-form/store/propertyStore";
import { useEffect, useMemo } from "react";
import dispatchMessagesOverlay from "@rf-l1/loading/mx-overlay-loading/util/dispatchMessagesOverlay";
import { PROPERTIES_MAIN_OVERLAY_ID } from "../../components/property-form/util/const/properties-overlays.const";
import toast from "react-hot-toast";
import {
  CPropertyForm,
  CPropertyFormKeys,
  IPropertyForm
} from "../../components/property-form/util/schemas/property-schema/propertySchema";
import usePermissionProperty from "../../hooks/usePermissionProperty/usePermissionProperty";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { HToastCatchServerError } from "@rf-helper/cath-error-server";

const PropertyUpdatePage = () => {
  const [UPDATE_PROPERTY_M] = useUpdateRealEstateMutation();
  const { id } = useParams();
  const navigate = useNavigate();
  const resetAllFormStates = useResetAllFormStates();
  const { getPermissions } = usePermissionProperty();
  const { data, status } = useGetDetailPropertyQuery(
    {
      params: {
        id: id!
      }
    },
    {
      skip: !id
    }
  );

  useEffect(() => {
    return () => {
      resetAllFormStates();
    };
  }, []);

  useEffect(() => {
    if (status == QueryStatus.fulfilled && data) {
      const permissions = getPermissions({
        idStatusProperty: data.status_id,
        isProject: false,
        agents: data.agents.map(agent => ({
          id: agent.id,
          agencyId: agent.agency_id
        }))
      });

      if (!permissions.canUpdateProperty) {
        navigate(`${C_PATH_ROUTES.REALESTATE.DETAIL(id!)}`, { replace: true });
        toast.error("No tienes permisos para modificar esta propiedad");
      }

      window.dispatchEvent(
        new CustomEvent("load-property", {
          detail: data
        })
      );
    }
  }, [status, data]);

  const handleSubmit = async (
    v: IPropertyForm,
    helper: UseFormReturn<IPropertyForm>
  ) => {
    try {
      const body = UpdatePropertyAdapter(v);
      dispatchMessagesOverlay(
        "Preparando actualización de la propiedad...",
        PROPERTIES_MAIN_OVERLAY_ID
      );

      const res = (await UPDATE_PROPERTY_M({
        body,
        params: {
          id: id!
        }
      })
        .unwrap()
        .catch(e => {
          HToastCatchServerError(e);
        })) as any;

      dispatchMessagesOverlay(
        "Actualizando propiedad...",
        PROPERTIES_MAIN_OVERLAY_ID
      );

      if (res?.data?.id) {
        helper.reset(CPropertyForm as unknown as IPropertyForm);

        dispatchMessagesOverlay(
          "Propiedad actualizado con éxito",
          PROPERTIES_MAIN_OVERLAY_ID
        );

        navigate(`${C_PATH_ROUTES.REALESTATE.DETAIL(id!)}`, {
          replace: true
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const stateForm = useMemo(() => {
    if (data) {
      if (data.project) {
        navigate(`${C_PATH_ROUTES.REALESTATE.PROJECT_UPDATE(id!)}`, {
          replace: true
        });
        return null;
      }
      const detailData = patchPropertyAdapter(data);
      return {
        ...detailData,
        [CPropertyFormKeys.is_project]: false,
        [CPropertyFormKeys.is_edit]: true
      } as unknown as IPropertyForm;
    }
    return null;
  }, [data]);

  if (status == QueryStatus.rejected)
    return (
      <MxEmptyCard size="page">
        <MxEmptyIcon variant="error" />
        <MxEmptyBody>
          Ah ocurrido un inconveniente o la propiedad no fue encontrada
        </MxEmptyBody>
      </MxEmptyCard>
    );

  if (status == QueryStatus.pending || !stateForm) {
    return (
      <ContentLoading>
        <MxLoading variant="gradient" />
      </ContentLoading>
    );
  }

  if (!data)
    return (
      <MxEmptyCard size="page">
        <MxEmptyIcon variant="404" />
        <MxEmptyBody>la propiedad no fue encontrada</MxEmptyBody>
      </MxEmptyCard>
    );

  return <PropertyFormGeneric state={stateForm} onSubmit={handleSubmit} />;
};

export default PropertyUpdatePage;

const ContentLoading = styled(Grid)`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  height: 70vh;
`;
